import React from 'react';
import { generateAspectInterpretation } from '../../lib/aspectInterpretation';
import { getPlanetGlyph, getAspectGlyph } from '../../lib/chartGlyphs';
import { PLANET_LABELS } from '../../lib/reportUtils';

const formatPlanetLabel = (key) => PLANET_LABELS[String(key || '').toLowerCase()] || key;

const labelMap = {
  conjunction: 'Conjunction',
  opposition: 'Opposition',
  trine: 'Trine',
  square: 'Square',
  sextile: 'Sextile',
};

const aspectHintMap = {
  conjunction: 'merged focus',
  opposition: 'polarity',
  trine: 'flow',
  square: 'friction',
  sextile: 'supportive opening',
};

function AspectInterpretationPanel({ aspect, chart }) {
  if (!chart) return null;
  const placements = chart.planets || [];
  const p1 = placements.find((p) => p.key === (aspect.p1 || '').toLowerCase());
  const p2 = placements.find((p) => p.key === (aspect.p2 || '').toLowerCase());
  const aspectWithLabels = {
    ...aspect,
    p1Label: aspect.p1Label || formatPlanetLabel(aspect.p1),
    p2Label: aspect.p2Label || formatPlanetLabel(aspect.p2),
  };
  const interp = generateAspectInterpretation({ aspect: aspectWithLabels, p1, p2 });
  if (!interp) return null;

  return (
    <div className="chart-accordion-panel" role="status" aria-live="polite">
      <h4 className="chart-accordion-panel-title">{interp.title}</h4>
      {interp.paragraphs.map((para, i) => (
        <p key={i} className="chart-accordion-panel-body">{para}</p>
      ))}
    </div>
  );
}

const AspectsTable = ({ aspects, selectedAspectId, onSelectAspect, chart }) => {
  const handleRowClick = (aspectId) => {
    onSelectAspect(aspectId === selectedAspectId ? null : aspectId);
  };

  return (
    <div className="chart-table-wrap card">
      <h3>Major aspects</h3>
      <p className="chart-help">A quick key: Trine = flow, Square = tension, Sextile = opportunity.</p>
      <p className="chart-interaction-hint">Select a row to expand the interpretation directly below it.</p>
      <table className="chart-table">
        <thead>
          <tr>
            <th>Planet A</th>
            <th>Aspect</th>
            <th>Planet B</th>
            <th>Orb</th>
          </tr>
        </thead>
        <tbody>
          {aspects.map((aspect, index) => (
            <React.Fragment key={aspect.id || `${aspect.p1}-${aspect.p2}-${index}`}>
              <tr
                className={aspect.id === selectedAspectId ? 'chart-row-selected' : ''}
                onClick={() => handleRowClick(aspect.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleRowClick(aspect.id);
                  }
                }}
              >
                <td><span className="chart-aspect-glyph">{getPlanetGlyph(aspect.p1)}</span> {aspect.p1Label}</td>
                <td>
                  <span className="chart-aspect-glyph">{getAspectGlyph(aspect.type)}</span> {labelMap[aspect.type] || aspect.type}
                  <span className="aspect-hint"> — {aspectHintMap[aspect.type] || 'relationship'}</span>
                </td>
                <td><span className="chart-aspect-glyph">{getPlanetGlyph(aspect.p2)}</span> {aspect.p2Label}</td>
                <td>{Number(aspect.orb || 0).toFixed(2)}°</td>
              </tr>
              {aspect.id === selectedAspectId && chart && (
                <tr className="chart-accordion-row">
                  <td colSpan={4} className="chart-accordion-cell">
                    <AspectInterpretationPanel aspect={aspect} chart={chart} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AspectsTable;
